import "./app.scss";

import { debounce } from "lodash";
import memoizeOne from "memoize-one";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { CharacterContext } from "../../components/character/CharacterContext";
import { ScreenContext } from "../../components/screen/ScreenContext";
import PubSubTopics from "../../constants/pubSubTopics";
import screenModes from "../../constants/screenModes";
import timeoutRates from "../../constants/timeoutRates";
import { appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import { characterWidthMapSelectors } from "../../modules/characterWidthMap";
import { mobileScreenOperations, mobileScreenSelectors } from "../../modules/mobileScreen";
import { preferencesSelectors } from "../../modules/preferences";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import isEditable from "../../utils/isEditable";
import userAgent from "../../utils/userAgent";
import windowDimensions from "../../utils/windowDimensions";
import SmartErrorBoundary from "../smartErrorBoundary";
import MobilePortraitApp from "./App.mobile";
import MobileLandscapeApp from "./App.mobileLandscape";
import TabletApp from "./App.tablet";

const getScreenContextValue = memoizeOne(
  (stateScreenMode, stateWidth, stateHeight, isEditMode, onEnterEditMode, onExitEditMode) => {
    return {
      screenMode: stateScreenMode,
      width: stateWidth,
      height: stateHeight,
      isEditMode: isEditMode,
      enterEditMode: onEnterEditMode,
      exitEditMode: onExitEditMode
    };
  }
);

class IosApp extends PureComponent {
  constructor(props, context) {
    super(props, context);
    const dimensions = windowDimensions();
    this.state = {
      screenMode: dimensions.screenMode,
      width: dimensions.width,
      height: dimensions.height
    };
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.getCharacterWidthMap = this.getCharacterWidthMap.bind(this);
    this.getFontSize = this.getFontSize.bind(this);
    this.getGlobalFontSize = this.getGlobalFontSize.bind(this);
    this.onEnterEditMode = this.onEnterEditMode.bind(this);
    this.onExitEditMode = this.onExitEditMode.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);

    //#!if mobile === true || harvard === true
    this.updateStoredMobileDimensions = this.updateStoredMobileDimensions.bind(this);
    //#!endif

    this.characterContextValue = {
      getWidthMap: this.getCharacterWidthMap,
      getFontSize: this.getFontSize,
      getGlobalFontSize: this.getGlobalFontSize
    };

    // Debouncing focus change so that we don't update the app for every focusout, focusin, etc.
    // call that occurs.
    this.debouncedFocusChange = debounce(this.onFocusChange, timeoutRates.APP_FOCUS_CHANGE_TIME);

    this._lastSkin = null;
    this._lastFontSize = null;
  }

  componentDidMount() {
    this.updateWindowDimensions();
    window.addEventListener("resize", this.updateWindowDimensions);
    window.addEventListener("focusin", this.debouncedFocusChange);
    window.addEventListener("focusout", this.debouncedFocusChange);

    document.body.classList.add("ios");
    //#!if mobile === true
// document.body.classList.add("mobile");
    //#!endif
    //#!if tablet === true
// document.body.classList.add("tablet");
    //#!endif

    if (userAgent.isScreenshotApp()) {
      document.body.classList.add("screenshot");
    }

    this.updateDocumentDom();

    //#!if mobile === true || harvard === true
    this.updateStoredMobileDimensions();
    //#!endif
  }

  componentDidUpdate() {
    this.updateDocumentDom();
    //#!if mobile === true || harvard === true
    this.updateStoredMobileDimensions();
    //#!endif
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateWindowDimensions);
    window.removeEventListener("focusin", this.debouncedFocusChange);
    window.removeEventListener("focusout", this.debouncedFocusChange);

    document.body.classList.remove("ios");
    //#!if mobile === true
// document.body.classList.remove("mobile");
    //#!endif
    //#!if tablet === true
// document.body.classList.remove("tablet");
    //#!endif
  }

  //#!if mobile === true || harvard === true
  updateStoredMobileDimensions() {
    const isMobilePortrait = this.state.screenMode === screenModes.MobilePortrait;
    if (
      this.state.width !== this.props.storedMobileWidth ||
      isMobilePortrait !== this.props.storedIsMobilePortraitMode
    ) {
      this.props.onMobileDimensionsChange(this.state.width, isMobilePortrait);
    }
  }
  //#!endif

  updateWindowDimensions() {
    const dimensions = windowDimensions();

    let updateState = false;
    let screenMode = this.state.screenMode;
    let width = this.state.width;
    let height = this.state.height;

    if (this.state.screenMode !== dimensions.screenMode) {
      screenMode = dimensions.screenMode;
      updateState = true;
    }
    if (this.state.width !== dimensions.width) {
      width = dimensions.width;
      updateState = true;
    }
    if (this.state.height !== dimensions.height) {
      height = dimensions.height;
      updateState = true;
    }

    if (updateState) {
      this.setState({ screenMode: screenMode, width: width, height: height });
    }

    // if (this.state.screenMode !== dimensions.screenMode) {
    //   this.setState({ screenMode: dimensions.screenMode });
    // }
    // if (this.state.width !== dimensions.width) {
    //   this.setState({ width: dimensions.width });
    // }
    // if (this.state.height !== dimensions.height) {
    //   this.setState({ height: dimensions.height });
    // }
  }

  updateDocumentDom() {
    if (this.props.globalFontSize !== this._lastFontSize) {
      document.getElementsByTagName("html")[0].style["font-size"] = `${this.props.globalFontSize}px`;
      this._lastFontSize = this.props.globalFontSize;
    }

    if (this.props.skin !== this._lastSkin) {
      if (this._lastSkin) {
        document.body.classList.remove(`theme-${this._lastSkin}`);
      }
      document.body.classList.add(`theme-${this.props.skin}`);

      this._lastSkin = this.props.skin;
    }
  }

  getCharacterWidthMap() {
    return this.props.characterWidthMap;
  }

  getFontSize() {
    return this.props.globalFontSize;
  }

  getGlobalFontSize() {
    return this.props.globalFontSize;
  }

  onEnterEditMode() {
    this.props.onEnterEditMode();
    let nativeMsg = {
      msg: nativeCommsBroadcastMessageTypes.EDIT_MODE_ENTERED
    };
    PubSub.publish(PubSubTopics.sendNativeMessage, nativeMsg);
  }

  onExitEditMode() {
    this.props.onExitEditMode();
    let nativeMsg = {
      msg: nativeCommsBroadcastMessageTypes.EDIT_MODE_EXITED
    };
    PubSub.publish(PubSubTopics.sendNativeMessage, nativeMsg);
  }

  onFocusChange(_eventOrMsg) {
    const fieldIsEditable =
      isEditable(document.activeElement) && !document.activeElement.classList.contains("tableCellClipboardCatcher");
    if (fieldIsEditable !== this._focusedFieldIsEditable) {
      PubSub.publish(PubSubTopics.sendNativeMessage, {
        msg: nativeCommsBroadcastMessageTypes.FOCUS_CHANGED,
        isEditable: fieldIsEditable
      });
      this._focusedFieldIsEditable = fieldIsEditable;
    }
  }

  render() {
    let appComponent = null;
    //#!if mobile === true || harvard === true
    appComponent =
      this.state.screenMode === screenModes.MobilePortrait ? (
        <MobilePortraitApp key="mobilePortraitApp" />
      ) : (
        <MobileLandscapeApp key="mobileLandscapeApp" />
      );
    //#!else
// appComponent = <TabletApp key="tabletApp" />;
    //#!endif

    return (
      <SmartErrorBoundary key="iosApp">
        <ScreenContext.Provider
          value={getScreenContextValue(
            this.state.screenMode,
            this.state.width,
            this.state.height,
            this.props.isEditMode,
            this.onEnterEditMode,
            this.onExitEditMode
          )}
        >
          <CharacterContext.Provider value={this.characterContextValue}>{appComponent}</CharacterContext.Provider>
        </ScreenContext.Provider>
      </SmartErrorBoundary>
    );
  }
}

IosApp.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  onEnterEditMode: PropTypes.func.isRequired,
  onExitEditMode: PropTypes.func.isRequired,
  characterWidthMap: ImmutablePropTypes.map.isRequired,
  fontSize: PropTypes.number.isRequired,
  globalFontSize: PropTypes.number.isRequired,
  skin: PropTypes.string.isRequired,

  // redux store dimensions
  //#!if mobile === true
// storedMobileWidth: PropTypes.number,
// storedIsMobilePortraitMode: PropTypes.bool,
// onMobileDimensionsChange: PropTypes.func
  //#!endif
};

const mapStateToProps = state => {
  return {
    isEditMode: uiStateSelectors.getIsEditMode(state.present),
    characterWidthMap: characterWidthMapSelectors.getCharacterWidthMap(state.present),
    fontSize: appStateSelectors.getFontSize(state.present),
    globalFontSize: preferencesSelectors.getGlobalFontSize(state.present),
    skin: preferencesSelectors.getSkin(state.present),

    //#!if mobile === true || harvard === true
    storedMobileWidth: mobileScreenSelectors.getMobileScreenWidth(state.present),
    storedIsMobilePortraitMode: mobileScreenSelectors.getIsMobilePortraitMode(state.present)
    //#!endif
  };
};

const readonlyActions = {
  //#!if mobile === true || harvard === true
  onMobileDimensionsChange: mobileScreenOperations.changeMobileScreenWidth
  //#!endif
};

const editingActions = {
  onEnterEditMode: uiStateOperations.enterEditMode,
  onExitEditMode: batchOperations.exitEditModeAndClearSelection
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(IosApp);
